import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Save,
  Upload,
  Camera,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building,
  DollarSign,
  Baby,
  Heart,
  Loader2,
  FileText,
  Download,
  Trash2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { employeesAPI } from "@/lib/api";
import { useContractPermissions } from "@/hooks/use-contract-permissions";
import { getAuthHeaders, getAuthHeadersMultipart } from "@/lib/auth-utils";

const EmployeeForm = ({ employee, onSuccess, departments = [] }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { canUploadContract, canDeleteContract, canViewContract } =
    useContractPermissions();
  const isEditing = !!employee;

  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [errors, setErrors] = useState({});
  const [photoFile, setPhotoFile] = useState(null);

  // Contract management state
  const [contractFile, setContractFile] = useState(null);
  const [contractPreview, setContractPreview] = useState(null);
  const [existingContract, setExistingContract] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    adresse: "",
    date_naissance: "",
    lieu_naissance: "",
    nationalite: "",
    date_embauche: "",
    immatriculation: "",
    departement_id: "",
    manager_id: "",
    role: "employe",
    type_contrat: "cdi",
    statut_employe: "actif",
    salaire: "",
    situation_familiale: "",
    nombre_enfants: 0,
    numero_securite_sociale: "",
    iban: "",
    initial_password: "",
    new_password: "",
    // Administrative documents
    cv_recu: false,
    contrat_signe_recu: false,
    photos_recu: false,
    cin_legalise_recu: false,
    fiche_anthropometrique_recu: false,
    diplomes_legalises_recu: false,
    certificats_travail_recu: false,
    cnss_carte_recu: false,
    rib_attestation_recu: false,
    cliche_pulmonaire_recu: false,
  });

  // Initialize form with employee data for editing
  useEffect(() => {
    if (employee) {
      setFormData({
        prenom: employee.prenom || "",
        nom: employee.nom || "",
        email: employee.email || "",
        telephone: employee.telephone || "",
        adresse: employee.adresse || "",
        date_naissance: employee.date_naissance
          ? new Date(employee.date_naissance).toISOString().split("T")[0]
          : "",
        lieu_naissance: employee.lieu_naissance || "",
        nationalite: employee.nationalite || "",
        date_embauche: employee.date_embauche
          ? new Date(employee.date_embauche).toISOString().split("T")[0]
          : "",
        immatriculation: employee.immatriculation || "",
        departement_id: employee.departement_id || "",
        manager_id: employee.manager_id || "",
        role: employee.role || "employe",
        type_contrat: employee.type_contrat || "cdi",
        statut_employe: employee.statut_employe || "actif",
        salaire: employee.salaire ? employee.salaire.toString() : "",
        situation_familiale: employee.situation_familiale || "",
        nombre_enfants: employee.nombre_enfants || 0,
        numero_securite_sociale: employee.numero_securite_sociale || "",
        iban: employee.iban || "",
        initial_password: "", // Never pre-fill password for security
        new_password: "", // Never pre-fill password for security
        // Administrative documents
        cv_recu: employee.cv_recu || false,
        contrat_signe_recu: employee.contrat_signe_recu || false,
        photos_recu: employee.photos_recu || false,
        cin_legalise_recu: employee.cin_legalise_recu || false,
        fiche_anthropometrique_recu: employee.fiche_anthropometrique_recu || false,
        diplomes_legalises_recu: employee.diplomes_legalises_recu || false,
        certificats_travail_recu: employee.certificats_travail_recu || false,
        cnss_carte_recu: employee.cnss_carte_recu || false,
        rib_attestation_recu: employee.rib_attestation_recu || false,
        cliche_pulmonaire_recu: employee.cliche_pulmonaire_recu || false,
      });

      // Set photo preview if exists
      if (employee.photo_profil) {
        setPhotoPreview(employee.photo_profil);
      }

      // Fetch existing contract if editing
      if (employee.id) {
        fetchExistingContract(employee.id);
      }
    } else {
      // Reset form for add mode
      setFormData({
        prenom: "",
        nom: "",
        email: "",
        telephone: "",
        adresse: "",
        date_naissance: "",
        lieu_naissance: "",
        nationalite: "",
        date_embauche: "",
        immatriculation: "",
        departement_id: "",
        manager_id: "",
        role: "employe",
        type_contrat: "cdi",
        statut_employe: "actif",
        salaire: "",
        situation_familiale: "",
        nombre_enfants: 0,
        numero_securite_sociale: "",
        iban: "",
        initial_password: "",
        new_password: "",
      });
      setPhotoPreview(null);
      setPhotoFile(null);
      setContractFile(null);
      setContractPreview(null);
      setExistingContract(null);
    }
    setErrors({});
  }, [employee]);

  // Fetch existing contract for editing mode
  const fetchExistingContract = async (employeeId) => {
    try {
      const response = await fetch(`/api/v1/employees/${employeeId}/contract`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.contract) {
          setExistingContract(data.contract);
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du contrat:", error);
    }
  };

  // Handle input changes
  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Simple validation function
  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.prenom.trim()) {
      newErrors.prenom = "Le prénom est requis";
    } else if (formData.prenom.trim().length < 2) {
      newErrors.prenom = "Le prénom doit contenir au moins 2 caractères";
    }

    if (!formData.nom.trim()) {
      newErrors.nom = "Le nom est requis";
    } else if (formData.nom.trim().length < 2) {
      newErrors.nom = "Le nom doit contenir au moins 2 caractères";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Veuillez entrer une adresse email valide";
    }

    if (!formData.date_embauche) {
      newErrors.date_embauche = "La date d'embauche est requise";
    }

    // Optional field validations
    if (formData.telephone && !/^[\d\s\-\+\(\)]+$/.test(formData.telephone)) {
      newErrors.telephone = "Numéro de téléphone invalide";
    }

    if (
      formData.salaire &&
      (isNaN(formData.salaire) || parseFloat(formData.salaire) < 0)
    ) {
      newErrors.salaire = "Le salaire doit être un nombre positif";
    }

    if (
      formData.nombre_enfants &&
      (isNaN(formData.nombre_enfants) || parseInt(formData.nombre_enfants) < 0)
    ) {
      newErrors.nombre_enfants =
        "Le nombre d'enfants doit être un nombre positif";
    }

    if (
      formData.date_naissance &&
      new Date(formData.date_naissance) >= new Date()
    ) {
      newErrors.date_naissance =
        "La date de naissance ne peut pas être dans le futur";
    }

    // Password validation (only for new employees if provided)
    if (
      !isEditing &&
      formData.initial_password &&
      formData.initial_password.length < 6
    ) {
      newErrors.initial_password =
        "Le mot de passe doit contenir au moins 6 caractères";
    }

    // New password validation (only for existing employees if provided)
    if (
      isEditing &&
      formData.new_password &&
      formData.new_password.length < 6
    ) {
      newErrors.new_password =
        "Le nouveau mot de passe doit contenir au moins 6 caractères";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle photo upload
  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner un fichier image valide",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Erreur",
        description: "L'image est trop volumineuse (5MB maximum)",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploadingPhoto(true);

      // Compress and resize image before storing
      const compressedFile = await compressImage(file);
      setPhotoFile(compressedFile);

      const objectUrl = URL.createObjectURL(compressedFile);
      setPhotoPreview(objectUrl);
    } catch (error) {
      console.error("Error processing photo:", error);
      toast({
        title: "Erreur",
        description: "Erreur lors du traitement de la photo",
        variant: "destructive",
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Compress image to reduce base64 size
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Resize image to maximum 400x400 while maintaining aspect ratio
        const maxSize = 400;
        let { width, height } = img;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: "image/jpeg",
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              reject(new Error("Failed to compress image"));
            }
          },
          "image/jpeg",
          0.8 // 80% quality
        );
      };

      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle contract file upload
  const handleContractUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    validateAndSetContractFile(file);
  };

  // Handle drag and drop for contract
  const handleContractDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    validateAndSetContractFile(file);
  };

  const handleContractDragOver = (event) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleContractDragLeave = (event) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  // Validate and set contract file
  const validateAndSetContractFile = (file) => {
    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Type de fichier non autorisé",
        description: "Formats acceptés : PDF, DOCX, DOC, JPG, PNG",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast({
        title: "Fichier trop volumineux",
        description: "Le fichier ne peut pas dépasser 5MB",
        variant: "destructive",
      });
      return;
    }

    setContractFile(file);

    // Create preview for images and PDFs
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setContractPreview({
          type: "image",
          url: e.target?.result,
          name: file.name,
          size: file.size,
        });
      };
      reader.readAsDataURL(file);
    } else {
      setContractPreview({
        type: file.type.includes("pdf") ? "pdf" : "document",
        name: file.name,
        size: file.size,
      });
    }
  };

  // Remove contract file
  const removeContractFile = () => {
    setContractFile(null);
    setContractPreview(null);
    // Reset file input
    const fileInput = document.getElementById("contract-upload");
    if (fileInput) fileInput.value = "";
  };

  // Download existing contract
  const downloadContract = async () => {
    if (!existingContract || !employee?.id) return;

    try {
      const response = await fetch(
        `/api/v1/employees/${employee.id}/contract/download`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = existingContract.nom || "contract.pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        toast({
          title: "Succès",
          description: "Contrat téléchargé avec succès",
        });
      } else {
        throw new Error("Erreur lors du téléchargement");
      }
    } catch (error) {
      console.error("Erreur lors du téléchargement du contrat:", error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le contrat",
        variant: "destructive",
      });
    }
  };

  // Delete existing contract
  const deleteContract = async () => {
    if (!existingContract || !employee?.id) return;

    try {
      const response = await fetch(
        `/api/v1/employees/${employee.id}/contract`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        }
      );

      if (response.ok) {
        setExistingContract(null);
        toast({
          title: "Succès",
          description: "Contrat supprimé avec succès",
        });
      } else {
        throw new Error("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du contrat:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le contrat",
        variant: "destructive",
      });
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if we have a valid employee ID for editing
    if (isEditing && (!employee || !employee.id)) {
      console.error("No valid employee ID for editing:", employee);
      toast({
        title: "Erreur",
        description: "ID d'employé manquant. Impossible de mettre à jour.",
        variant: "destructive",
      });
      return;
    }

    if (!validateForm()) {
      toast({
        title: "Erreur",
        description: "Veuillez corriger les erreurs avant de soumettre",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      // Prepare data for submission
      const submitData = { ...formData };

      // Convert date strings to proper format
      if (submitData.date_naissance) {
        submitData.date_naissance = new Date(
          submitData.date_naissance
        ).toISOString();
      }
      if (submitData.date_embauche) {
        submitData.date_embauche = new Date(
          submitData.date_embauche
        ).toISOString();
      }

      // Convert numeric fields
      if (submitData.salaire) {
        submitData.salaire = parseFloat(submitData.salaire);
      } else {
        submitData.salaire = null;
      }

      // Ensure nombre_enfants is always an integer
      if (
        submitData.nombre_enfants !== undefined &&
        submitData.nombre_enfants !== null &&
        submitData.nombre_enfants !== ""
      ) {
        submitData.nombre_enfants = parseInt(submitData.nombre_enfants) || 0;
      } else {
        submitData.nombre_enfants = 0;
      }

      // Handle empty strings and convert to null for optional fields
      Object.keys(submitData).forEach((key) => {
        if (submitData[key] === "" || submitData[key] === "none") {
          submitData[key] = null;
        }
      });

      // Remove undefined or empty department
      if (!submitData.departement_id || submitData.departement_id === "") {
        submitData.departement_id = null;
      }

      if (isEditing) {
        // When editing, use FormData to handle both photo and contract files
        const fd = new FormData();
        Object.entries(submitData).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            fd.append(key, value);
          }
        });

        // Append photo file if provided
        if (photoFile) {
          fd.append("photo", photoFile);
        }

        // Append contract file if provided
        if (contractFile) {
          fd.append("contract", contractFile);
        }

        await employeesAPI.update(employee.id, fd);
        toast({
          title: "Succès",
          description: "Employé mis à jour avec succès",
        });
      } else {
        // For creation, use FormData to handle both photo and contract files
        const fd = new FormData();
        Object.entries(submitData).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            fd.append(key, value);
          }
        });

        // Append photo file if provided
        if (photoFile) {
          fd.append("photo", photoFile);
        }

        // Append contract file if provided
        if (contractFile) {
          fd.append("contract", contractFile);
        }

        await employeesAPI.create(fd);
        toast({
          title: "Succès",
          description: "Employé créé avec succès",
        });
      }

      onSuccess?.();
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Erreur",
        description:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Erreur lors de la sauvegarde",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ""}${lastName?.charAt(0) || ""
      }`.toUpperCase();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Photo Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Photo de profil
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={photoPreview} />
              <AvatarFallback className="text-lg">
                {getInitials(formData.prenom, formData.nom)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <Label htmlFor="photo-upload" className="cursor-pointer">
                <div className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Télécharger une photo
                </div>
              </Label>
              <input
                id="photo-upload"
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                disabled={uploadingPhoto}
              />
              {uploadingPhoto && (
                <div className="flex items-center gap-2 mt-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">
                    Téléchargement...
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contract Upload Section */}
      {(canUploadContract() ||
        (isEditing && canViewContract(employee?.id))) && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Contrat de travail
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Existing Contract Display */}
              {existingContract && (
                <div className="p-4 border rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{existingContract.nom}</p>
                        <p className="text-sm text-muted-foreground">
                          Ajouté le{" "}
                          {new Date(
                            existingContract.created_at
                          ).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={downloadContract}
                        disabled={!canViewContract(employee?.id)}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Télécharger
                      </Button>
                      {canDeleteContract(employee?.id) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={deleteContract}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Supprimer
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Contract Upload Area */}
              {canUploadContract() && (
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${isDragActive
                    ? "border-primary bg-primary/10"
                    : "border-muted-foreground/25 hover:border-primary/50"
                    }`}
                  onClick={() =>
                    document.getElementById("contract-upload").click()
                  }
                  onDrop={handleContractDrop}
                  onDragOver={handleContractDragOver}
                  onDragLeave={handleContractDragLeave}
                >
                  {contractPreview ? (
                    <div className="space-y-4">
                      {/* Contract Preview */}
                      <div className="flex items-center justify-center">
                        {contractPreview.type === "image" ? (
                          <img
                            src={contractPreview.url}
                            alt="Aperçu contrat"
                            className="max-w-48 max-h-32 object-cover rounded border"
                          />
                        ) : (
                          <div className="flex items-center gap-2 p-4 border rounded bg-muted">
                            <FileText className="h-8 w-8 text-muted-foreground" />
                            <div className="text-left">
                              <p className="font-medium">
                                {contractPreview.name}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {formatFileSize(contractPreview.size)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={removeContractFile}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Supprimer
                        </Button>

                        <Label
                          htmlFor="contract-upload"
                          className="cursor-pointer"
                        >
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            asChild
                          >
                            <span>
                              <Upload className="h-4 w-4 mr-1" />
                              Changer
                            </span>
                          </Button>
                        </Label>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="h-8 w-8 mx-auto text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">
                          Glissez-déposez un fichier contrat ou{" "}
                          <Label
                            htmlFor="contract-upload"
                            className="text-primary cursor-pointer hover:underline"
                          >
                            parcourez
                          </Label>
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Formats acceptés: PDF, DOCX, DOC, JPG, PNG (max 5MB)
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Input masqué déclenché par clic sur le conteneur */}
              <input
                id="contract-upload"
                type="file"
                accept=".pdf,.docx,.doc,.jpg,.jpeg,.png"
                onChange={handleContractUpload}
                className="hidden"
                disabled={!canUploadContract()}
              />
            </CardContent>
          </Card>
        )}

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Informations personnelles
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="prenom">Prénom *</Label>
              <Input
                id="prenom"
                value={formData.prenom}
                onChange={(e) => handleInputChange("prenom", e.target.value)}
                placeholder="Entrez le prénom"
                className={errors.prenom ? "border-red-500" : ""}
              />
              {errors.prenom && (
                <p className="text-sm text-red-500 mt-1">{errors.prenom}</p>
              )}
            </div>

            <div>
              <Label htmlFor="nom">Nom *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => handleInputChange("nom", e.target.value)}
                placeholder="Entrez le nom"
                className={errors.nom ? "border-red-500" : ""}
              />
              {errors.nom && (
                <p className="text-sm text-red-500 mt-1">{errors.nom}</p>
              )}
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="Entrez l'email"
                className={errors.email ? "border-red-500" : ""}
              // disabled={isEditing} // Disable email editing
              />
              {errors.email && (
                <p className="text-sm text-red-500 mt-1">{errors.email}</p>
              )}
            </div>

            <div>
              <Label htmlFor="telephone">Téléphone</Label>
              <Input
                id="telephone"
                value={formData.telephone}
                onChange={(e) => handleInputChange("telephone", e.target.value)}
                placeholder="Entrez le numéro de téléphone"
                className={errors.telephone ? "border-red-500" : ""}
              />
              {errors.telephone && (
                <p className="text-sm text-red-500 mt-1">{errors.telephone}</p>
              )}
            </div>

            <div>
              <Label htmlFor="date_naissance">Date de naissance</Label>
              <Input
                id="date_naissance"
                type="date"
                value={formData.date_naissance}
                onChange={(e) =>
                  handleInputChange("date_naissance", e.target.value)
                }
                className={errors.date_naissance ? "border-red-500" : ""}
              />
              {errors.date_naissance && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.date_naissance}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="date_embauche">Date d'embauche *</Label>
              <Input
                id="date_embauche"
                type="date"
                value={formData.date_embauche}
                onChange={(e) =>
                  handleInputChange("date_embauche", e.target.value)
                }
                className={errors.date_embauche ? "border-red-500" : ""}
              />
              {errors.date_embauche && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.date_embauche}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="immatriculation">Immatriculation</Label>
              <Input
                id="immatriculation"
                value={formData.immatriculation}
                onChange={(e) =>
                  handleInputChange("immatriculation", e.target.value)
                }
                placeholder="Entrez l'immatriculation"
              />
            </div>

            <div>
              <Label htmlFor="lieu_naissance">Lieu de naissance</Label>
              <Input
                id="lieu_naissance"
                value={formData.lieu_naissance}
                onChange={(e) =>
                  handleInputChange("lieu_naissance", e.target.value)
                }
                placeholder="Entrez le lieu de naissance"
              />
            </div>

            <div>
              <Label htmlFor="nationalite">Nationalité</Label>
              <Input
                id="nationalite"
                value={formData.nationalite}
                onChange={(e) =>
                  handleInputChange("nationalite", e.target.value)
                }
                placeholder="Entrez la nationalité"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="adresse">Adresse</Label>
            <Textarea
              id="adresse"
              value={formData.adresse}
              onChange={(e) => handleInputChange("adresse", e.target.value)}
              placeholder="Entrez l'adresse complète"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Professional Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Informations professionnelles
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="departement_id">Département</Label>
              <Select
                value={formData.departement_id || "none"}
                onValueChange={(value) =>
                  handleInputChange(
                    "departement_id",
                    value === "none" ? "" : value
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un département" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun département</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="role">Rôle</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => handleInputChange("role", value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrateur</SelectItem>
                  <SelectItem value="rh">Ressources Humaines</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="employe">Employé</SelectItem>
                  <SelectItem value="stagiaire">Stagiaire</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="type_contrat">Type de contrat</Label>
              <Select
                value={formData.type_contrat}
                onValueChange={(value) =>
                  handleInputChange("type_contrat", value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cdi">CDI</SelectItem>
                  <SelectItem value="cdd">CDD</SelectItem>
                  <SelectItem value="stage">Stage</SelectItem>
                  <SelectItem value="interim">Intérim</SelectItem>
                  <SelectItem value="freelance">Freelance</SelectItem>
                  <SelectItem value="apprentissage">Apprentissage</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="statut_employe">Statut de l'employé</Label>
              <Select
                value={formData.statut_employe}
                onValueChange={(value) =>
                  handleInputChange("statut_employe", value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="actif">Actif</SelectItem>
                  <SelectItem value="conge">En congé</SelectItem>
                  <SelectItem value="suspendu">Suspendu</SelectItem>
                  <SelectItem value="demissionne">Démissionnaire</SelectItem>
                  <SelectItem value="licencie">Licencié</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!isEditing && (
              <div>
                <Label htmlFor="initial_password">
                  Mot de passe initial (optionnel)
                </Label>
                <Input
                  id="initial_password"
                  type="password"
                  value={formData.initial_password}
                  onChange={(e) =>
                    handleInputChange("initial_password", e.target.value)
                  }
                  placeholder="Laissez vide pour générer automatiquement"
                  className={errors.initial_password ? "border-red-500" : ""}
                />
                {errors.initial_password && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.initial_password}
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-1">
                  Si aucun mot de passe n'est fourni, un mot de passe temporaire
                  sera généré automatiquement
                </p>
              </div>
            )}

            {isEditing && (
              <div>
                <Label htmlFor="new_password">
                  Nouveau mot de passe (optionnel)
                </Label>
                <Input
                  id="new_password"
                  type="password"
                  value={formData.new_password}
                  onChange={(e) =>
                    handleInputChange("new_password", e.target.value)
                  }
                  placeholder="Laissez vide pour conserver le mot de passe actuel"
                  className={errors.new_password ? "border-red-500" : ""}
                />
                {errors.new_password && (
                  <p className="text-sm text-red-500 mt-1">
                    {errors.new_password}
                  </p>
                )}
                <p className="text-sm text-muted-foreground mt-1">
                  Entrez un nouveau mot de passe seulement si vous souhaitez le
                  changer
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="salaire">Salaire</Label>
              <Input
                id="salaire"
                type="number"
                step="0.01"
                value={formData.salaire}
                onChange={(e) => handleInputChange("salaire", e.target.value)}
                placeholder="Entrez le salaire"
                className={errors.salaire ? "border-red-500" : ""}
              />
              {errors.salaire && (
                <p className="text-sm text-red-500 mt-1">{errors.salaire}</p>
              )}
            </div>

            <div>
              <Label htmlFor="numero_securite_sociale">
                Numéro de sécurité sociale
              </Label>
              <Input
                id="numero_securite_sociale"
                value={formData.numero_securite_sociale}
                onChange={(e) =>
                  handleInputChange("numero_securite_sociale", e.target.value)
                }
                placeholder="Entrez le numéro de sécurité sociale"
              />
            </div>

            <div>
              <Label htmlFor="iban">IBAN</Label>
              <Input
                id="iban"
                value={formData.iban}
                onChange={(e) => handleInputChange("iban", e.target.value)}
                placeholder="Entrez l'IBAN"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Family Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5" />
            Informations familiales
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="situation_familiale">Situation familiale</Label>
              <Select
                value={formData.situation_familiale || "none"}
                onValueChange={(value) =>
                  handleInputChange(
                    "situation_familiale",
                    value === "none" ? "" : value
                  )
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner la situation familiale" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Non spécifié</SelectItem>
                  <SelectItem value="celibataire">Célibataire</SelectItem>
                  <SelectItem value="marie">Marié(e)</SelectItem>
                  <SelectItem value="divorce">Divorcé(e)</SelectItem>
                  <SelectItem value="veuf">Veuf/Veuve</SelectItem>
                  <SelectItem value="pacse">Pacsé(e)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="nombre_enfants">Nombre d'enfants</Label>
              <Input
                id="nombre_enfants"
                type="number"
                min="0"
                value={formData.nombre_enfants}
                onChange={(e) =>
                  handleInputChange("nombre_enfants", e.target.value)
                }
                placeholder="0"
                className={errors.nombre_enfants ? "border-red-500" : ""}
              />
              {errors.nombre_enfants && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.nombre_enfants}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents administratifs */}
      {isEditing && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Documents administratifs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* CV */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="cv_recu"
                  checked={formData.cv_recu}
                  onCheckedChange={(checked) =>
                    handleInputChange("cv_recu", checked)
                  }
                />
                <label
                  htmlFor="cv_recu"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  CV reçu
                </label>
              </div>

              {/* Contrat signé */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="contrat_signe_recu"
                  checked={formData.contrat_signe_recu}
                  onCheckedChange={(checked) =>
                    handleInputChange("contrat_signe_recu", checked)
                  }
                />
                <label
                  htmlFor="contrat_signe_recu"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Contrat signé reçu
                </label>
              </div>

              {/* Photos */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="photos_recu"
                  checked={formData.photos_recu}
                  onCheckedChange={(checked) =>
                    handleInputChange("photos_recu", checked)
                  }
                />
                <label
                  htmlFor="photos_recu"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  2 Photos reçues
                </label>
              </div>

              {/* CIN légalisée */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="cin_legalise_recu"
                  checked={formData.cin_legalise_recu}
                  onCheckedChange={(checked) =>
                    handleInputChange("cin_legalise_recu", checked)
                  }
                />
                <label
                  htmlFor="cin_legalise_recu"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Copie CIN légalisée reçue
                </label>
              </div>

              {/* Fiche anthropométrique */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="fiche_anthropometrique_recu"
                  checked={formData.fiche_anthropometrique_recu}
                  onCheckedChange={(checked) =>
                    handleInputChange("fiche_anthropometrique_recu", checked)
                  }
                />
                <label
                  htmlFor="fiche_anthropometrique_recu"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Fiche anthropométrique reçue
                </label>
              </div>

              {/* Diplômes légalisés */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="diplomes_legalises_recu"
                  checked={formData.diplomes_legalises_recu}
                  onCheckedChange={(checked) =>
                    handleInputChange("diplomes_legalises_recu", checked)
                  }
                />
                <label
                  htmlFor="diplomes_legalises_recu"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Diplômes légalisés reçus
                </label>
              </div>

              {/* Certificats de travail */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="certificats_travail_recu"
                  checked={formData.certificats_travail_recu}
                  onCheckedChange={(checked) =>
                    handleInputChange("certificats_travail_recu", checked)
                  }
                />
                <label
                  htmlFor="certificats_travail_recu"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Certificats de travail reçus
                </label>
              </div>

              {/* Carte CNSS */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="cnss_carte_recu"
                  checked={formData.cnss_carte_recu}
                  onCheckedChange={(checked) =>
                    handleInputChange("cnss_carte_recu", checked)
                  }
                />
                <label
                  htmlFor="cnss_carte_recu"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Carte CNSS / Bulletin de paie reçu
                </label>
              </div>

              {/* Attestation RIB */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rib_attestation_recu"
                  checked={formData.rib_attestation_recu}
                  onCheckedChange={(checked) =>
                    handleInputChange("rib_attestation_recu", checked)
                  }
                />
                <label
                  htmlFor="rib_attestation_recu"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Attestation RIB reçue
                </label>
              </div>

              {/* Cliché pulmonaire */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="cliche_pulmonaire_recu"
                  checked={formData.cliche_pulmonaire_recu}
                  onCheckedChange={(checked) =>
                    handleInputChange("cliche_pulmonaire_recu", checked)
                  }
                />
                <label
                  htmlFor="cliche_pulmonaire_recu"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Cliché pulmonaire reçu
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Form Actions */}
      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          disabled={loading}
          className="flex items-center gap-2"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {isEditing ? "Mettre à jour" : "Créer"}
        </Button>
      </div>
    </form>
  );
};

export default EmployeeForm;
